import * as React from 'react';

import _ from 'lodash';
import { compose } from 'recompose';
import connectToStores from 'vendor/cnpm/fluxible.v0-4/addons/connectToStores';

import type { ActionContext } from 'js/lib/ActionContext';
import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { Button, Typography } from '@coursera/cds-core';

import Modal from 'bundles/phoenix/components/Modal';

import _t from 'i18n!nls/staff-management';

import 'css!./__styles__/DelModal';

import PillList from './common/PillList';
import withNotifications from './common/notifications/withNotifications';
import type { StaffManagementStoreV2 as StaffManagementStoreClass, StaffMembershipType } from './store';
import { actions, selectors } from './store';
import withFluxibleContextAsProps from './withFluxibleContextAsProps';
import withShow from './withShow';

type Props = {
  /** Called when user clicks cancel button. */
  cancelOnClick?: () => void;
  /** If true, cancel button cannot be clicked (no callback). */
  cancelDisabled?: boolean;

  /** Called when user clicks confirm button. */
  confirmOnClick?: () => void;
  /** If true, confirm button cannot be clicked (no callback). */
  confirmDisabled?: boolean;

  /** Will this action remove all roles these users have (modifies messaging)? */
  fullDelete?: boolean;

  /** Names of people being deleted. */
  names?: Array<string>;

  /** One of the types supported by the staff-membership component. Modifies messaging shown. */
  type?: keyof typeof StaffMembershipType;

  /** Creates a new toast notification */
  notify: () => void;
};

/**
 * Blocking confirmation modal, displayed at the beginning of a delete flow and used to
 * warn the user of the consequences of their actions.
 */
export const DelModal = ({
  fullDelete = false,
  names = [],
  cancelOnClick = _.noop,
  cancelDisabled = false,
  confirmOnClick = _.noop,
  confirmDisabled = false,
  type = 'partner' as const,
}: Props) => {
  let title = '';
  let contentMessage = '';
  let footerMessage = _t('To re-join, they will need to be added again.');
  if (type === 'partner') {
    title = _t('Remove institution roles?');
    contentMessage = _t(
      `Are you sure you want to remove {numItems, plural, =1 {this staff member's} other {these staff members'}} institution roles?`
    );
    if (fullDelete) {
      title = _t('Remove staff?');
      footerMessage = _t(
        'This will also remove them from any assigned programs and courses. To re-join, they will need to be added again.'
      );
    }
  } else if (type === 'program') {
    title = _t('Remove program roles?');
    contentMessage = _t(
      `Are you sure you want to remove {numItems, plural, =1 {this staff member's} other {these staff members'}} program roles?`
    );
  }

  return (
    // At time of writing modal does not allow pass through of additional properties. To allow selection of modal components
    // we wrap entire contents in `data-e2e` tag.
    <div data-e2e={DelModal.e2eName}>
      <Modal
        className="rc-DelModal"
        // This gets dropped into an `aria-label` on the modal. This should be translated and in plain language.
        modalName={title}
        allowClose={false}
      >
        <Typography className="title" variant="h1" component="h2">
          {title}
        </Typography>

        <p className="content">
          <FormattedMessage message={contentMessage} numItems={names.length} />
        </p>

        <PillList items={names} />

        <p className="content">{footerMessage}</p>

        <div className="footer">
          <Button
            disabled={cancelDisabled}
            data-e2e="btn-cancel"
            onClick={cancelOnClick}
            className="button"
            variant="primary"
          >
            {_t('Cancel')}
          </Button>
          <Button
            disabled={confirmDisabled}
            onClick={confirmOnClick}
            data-e2e="btn-confirm"
            className="button"
            variant="primary"
          >
            {_t('Remove')}
          </Button>
        </div>
      </Modal>
    </div>
  );
};

DelModal.e2eName = 'staff-management-del-modal';

export const storeConnector = (
  { StaffManagementStoreV2 }: { StaffManagementStoreV2: StaffManagementStoreClass },
  { executeAction, notify }: $TSFixMe
) => {
  const state = StaffManagementStoreV2.get();
  const users = state.ui?.delFlow?.users;
  const membershipsToDelete = state.ui?.delFlow?.memberships;
  const type = state.ui?.delFlow?.type;

  let fullDelete = true;
  // A "full delete" of all memberships within scope is implied when there are no specific
  // specific membershipsToDelete. However, it is possible to wind up here and only
  // delete a subset of in-scope memberships, but only for a single user. This
  // nuance only matters in the messaging displayed to the user.
  if (membershipsToDelete?.length && users?.length === 1) {
    const membershipsByUserId = selectors.staffMemberships.getMembershipsByUserId(state);
    if (users[0]?.id && membershipsByUserId[users[0].id].length !== membershipsToDelete.length) {
      fullDelete = false;
    }
  }
  return {
    cancelOnClick: () => {
      return executeAction(actions.delFlow.prompt);
    },
    confirmOnClick: async () => {
      executeAction(actions.delFlow.confirm, { notify });
    },
    fullDelete,
    names: _.map(users, (user) => user.fullName),
    show: !!users?.length,
    type,
  };
};

export default compose<Props, {}>(
  withFluxibleContextAsProps(),
  withNotifications(),
  connectToStores<Props, Props & ActionContext>(['StaffManagementStoreV2'], storeConnector),
  withShow()
)(DelModal);
